import * as i0 from '@angular/core';
import { Injectable, inject, Optional } from '@angular/core';
import { isSupported, Analytics, logEvent, setUserId, setUserProperties, setAnalyticsCollectionEnabled } from '@angular/fire/analytics';
import { Auth, fetchSignInMethodsForEmail } from '@angular/fire/auth';
import * as i1 from '@engineering11/web-api-error';
import { toE11Error, STANDARD_ERROR_MESSAGE, E11_STANDARD_ERRORS, e11Error } from '@engineering11/web-api-error';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { Storage, ref, uploadBytesResumable, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { omit, retryWithExponentialBackoff, RedBlackTree, filterNullish } from '@engineering11/utility';
import { isSupported as isSupported$1, RemoteConfig, getAll, getValue, fetchAndActivate } from '@angular/fire/remote-config';
import { increment, serverTimestamp, documentId, arrayUnion, arrayRemove, deleteField, where, orderBy, limit, limitToLast, startAt, startAfter, endAt, endBefore, Firestore, doc, collection, onSnapshot, getDoc, getDocs, query, getCountFromServer, setDoc, addDoc, updateDoc, deleteDoc, runTransaction, collectionGroup } from '@angular/fire/firestore';
import { BehaviorSubject, Subject, from, of, Observable, iif, firstValueFrom, map as map$1, catchError as catchError$1 } from 'rxjs';
import { map, scan, tap, take, shareReplay, filter, withLatestFrom, switchMap, catchError } from 'rxjs/operators';
import { DateService } from '@engineering11/date-time';
import { openDB } from 'idb';
import { isNil, last } from 'lodash';

/**
 * Wrapper around AngularFireAnalytics so we can more easily update it and expand capabilities in future if desired
 * The application must first set up Google Analytics in the AppModule: https://github.com/angular/angularfire/blob/master/docs/analytics/getting-started.md
 */
class AnalyticsService {
    constructor(injector) {
        this.injector = injector;
        this.analyticsPromise = this.initAnalytics();
    }
    async initAnalytics() {
        const supported = await isSupported();
        return supported ? this.injector.get(Analytics) : undefined;
    }
    async handle(cb, ...args) {
        const analytics = await this.analyticsPromise;
        if (analytics) {
            return cb(analytics, ...args);
        }
        return undefined;
    }
    /**
     * When creating events, first review the recommended events and use those if appropriate https://developers.google.com/analytics/devguides/collection/ga4/events
     * Each application should maintain constants & enums organising the following custom eventParams:
     * - Platform (which app did this event come from)
     * - Module (location of component)
     * These params should be passed in at runtime on each event.
     * @param eventName
     * @param eventParams
     * @param options
     * @returns
     */
    logEvent(eventName, eventParams, options) {
        return this.handle(logEvent, eventName, eventParams, options);
    }
    /**
     *
     * @param {string} id user id
     * @param {AnalyticsCallOptions} options Google Analytics options
     * @returns
     */
    setUserId(id, options) {
        return this.handle(setUserId, id, options);
    }
    /**
     *
     * @param {CustomParams} properties Google Analytics properties to set
     * @param {AnalyticsCallOptions} options Google Analytics options
     * @returns
     */
    setUserProperties(properties, options) {
        return this.handle(setUserProperties, properties, options);
    }
    /**
     *
     * @param {boolean} enabled Google Analytics collection enabled
     * @returns
     */
    setAnalyticsCollectionEnabled(enabled) {
        return this.handle(setAnalyticsCollectionEnabled, enabled);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AnalyticsService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AnalyticsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });

class AuthUtilService {
    constructor(errorHandler) {
        this.errorHandler = errorHandler;
        this.auth = inject(Auth);
    }
    async checkEmailAvailability(email) {
        let { res, err: _ } = await this.errorHandler.asyncHandler(fetchSignInMethodsForEmail(this.auth, email.trim()));
        if (res && res.length > 0) {
            return false;
        }
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AuthUtilService, deps: [{ token: i1.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AuthUtilService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AuthUtilService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.E11ErrorHandlerService }] });

class FirebaseFunctionsService {
    static initializeFunction() {
        this.functionInstance = getFunctions();
    }
    static get function() {
        if (!this.functionInstance) {
            this.initializeFunction();
        }
        return this.functionInstance;
    }
    static httpsCallable(functionName) {
        return httpsCallable(this.function, functionName);
    }
    static async call(functionName, param) {
        const callable = this.httpsCallable(functionName);
        const result = await callable(param);
        return result.data;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FirebaseFunctionsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FirebaseFunctionsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FirebaseFunctionsService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const API_ROOT = 'web-api-firebase';
const ERROR_TYPES = Object.freeze({
    DOCUMENT_NOT_FOUND: `${API_ROOT}/document-not-found`,
    DOCUMENT_MISSING_ID: `${API_ROOT}/document-missing-id`,
    STORAGE_UPLOAD_FAILED: `storage/upload/unknown`,
    STORAGE_UPLOAD_UNAUTHORIZED: `storage/upload/unauthorized`,
    STORAGE_DELETE_FAILED: `storage/delete/unknown`,
    FIRESTORE_UNKNOWN: 'firestore/unknown',
});

class StorageService {
    constructor() {
        this.storage = inject(Storage);
    }
    /**
     * Uploads a file to Firebase Storage.
     *
     * @param file The file to upload.
     * @param path The storage path for the file.
     * @param resumable Whether to use resumable uploads (default is false).
     * @returns A promise that resolves with the upload result.
     */
    async upload(file, path, config) {
        try {
            const { resumable = false } = config || {};
            const storageRef = ref(this.storage, path);
            const uploadConfig = { ...(config && omit(config, 'resumable')) };
            const res = await (resumable ? uploadBytesResumable : uploadBytes)(storageRef, file, uploadConfig);
            const downloadURL = await getDownloadURL(res.ref);
            const { metadata: { fullPath, name, size }, } = res;
            return { downloadURL, fileName: name, fullPath, size };
        }
        catch (e) {
            throw this.handleUploadErr(e, { file, path });
        }
    }
    /**
     * Uploads a file to Firebase Storage with metadata.
     *
     * @param file The file to upload.
     * @param path The storage path for the file.
     * @param metaData The metadata to include with the file.
     * @param resumable Whether to use resumable uploads (default is false).
     * @returns A promise that resolves with the upload result.
     */
    async uploadWithMetadata(file, path, metaData, config) {
        try {
            const { resumable = false } = config || {};
            const storageRef = ref(this.storage, path);
            const uploadConfig = { ...(config && omit(config, 'resumable')), customMetadata: metaData };
            const res = await (resumable ? uploadBytesResumable : uploadBytes)(storageRef, file, uploadConfig);
            const downloadURL = await getDownloadURL(res.ref);
            const { metadata: { name, fullPath, size }, } = res;
            return { downloadURL, fileName: name, fullPath, size };
        }
        catch (e) {
            throw this.handleUploadErr(e, { file, path, metaData });
        }
    }
    /**
     * Deletes a file from Firebase Storage.
     *
     * @param path The path of the file to delete.
     * @returns A promise that resolves when the file is successfully deleted.
     */
    async delete(path) {
        try {
            const storageRef = ref(this.storage, path);
            await deleteObject(storageRef);
        }
        catch (e) {
            this.handleDeleteErr(e, { path });
        }
    }
    /**
     * Handles upload-related errors.
     *
     * @param e The error thrown during the upload.
     * @param additionalData Additional context about the error.
     * @returns An `E11Error` object describing the error.
     */
    handleUploadErr(e, additionalData) {
        if (e && typeof e === 'object' && 'code' in e && e.code === 'storage/unauthorized') {
            return toE11Error(e, {
                title: STANDARD_ERROR_MESSAGE,
                type: ERROR_TYPES.STORAGE_UPLOAD_UNAUTHORIZED,
                message: 'Storage security rules rejected the upload.',
                additionalData,
            });
        }
        return toE11Error(e, {
            title: STANDARD_ERROR_MESSAGE,
            type: ERROR_TYPES.STORAGE_UPLOAD_FAILED,
            additionalData,
        });
    }
    /**
     * Handles delete-related errors.
     *
     * @param e The error thrown during the delete operation.
     * @param additionalData Additional context about the error.
     */
    handleDeleteErr(e, additionalData) {
        if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' && e.message.includes('does not exist')) {
            return; // Silently ignore non-existent file errors.
        }
        throw toE11Error(e, {
            title: STANDARD_ERROR_MESSAGE,
            type: ERROR_TYPES.STORAGE_DELETE_FAILED,
            additionalData,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: StorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: StorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: StorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

// With exponential backoff algo, this is capped at 1.5 seconds total
const maxFetchAttempts = 5;
const initialBackoffMs = 100;
class RemoteConfigService {
    constructor(injector, errorHandler) {
        this.injector = injector;
        this.errorHandler = errorHandler;
        this.remoteConfigPromise = this.initRemoteConfig();
        this.resultOfFetch = this.remoteConfigPromise.then((config) => this.refreshFromRemote(config)); // Should be tantamount to https://firebase.google.com/docs/remote-config/loading#strategy_1_fetch_and_activate_on_load
    }
    async initRemoteConfig() {
        const supported = await isSupported$1();
        if (!supported)
            return undefined;
        return this.injector.get(RemoteConfig);
    }
    /**
     * Visual results of calls to this should sit behind loader
     * @returns
     */
    async getAll() {
        return this.remoteConfigHandler(getAll);
    }
    /**
     *
     * @param {string} key key value to get from remote config
     * @returns
     */
    async getValue(key) {
        return this.remoteConfigHandler(getValue, key);
    }
    async remoteConfigHandler(cb, ...args) {
        const remoteConfig = await this.remoteConfigPromise;
        await this.resultOfFetch;
        if (remoteConfig) {
            return await cb(remoteConfig, ...args);
        }
        return undefined;
    }
    /**
     * Use sparingly. There is a throttle.
     *
     * @returns
     */
    async refreshFromRemote(remoteConfig) {
        if (!remoteConfig)
            return Promise.resolve(false);
        try {
            const fetchAndActivateWithBackoff = retryWithExponentialBackoff(() => fetchAndActivate(remoteConfig), maxFetchAttempts, initialBackoffMs);
            return await fetchAndActivateWithBackoff();
        }
        catch (err) {
            this.errorHandler.handleError(this.toE11RemoteConfigError(err));
            return Promise.resolve(false);
        }
    }
    toE11RemoteConfigError(err) {
        if (err.message.includes('remoteconfig/storage-open'))
            return toE11Error(err, { title: 'Error using browser storage.', type: 'config/remote-config-storage-open' });
        return toE11Error(err, { title: 'Error fetching configuration data.', type: 'config/remote-config-failure' });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RemoteConfigService, deps: [{ token: i0.Injector }, { token: i1.E11ErrorHandlerService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RemoteConfigService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RemoteConfigService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i1.E11ErrorHandlerService }] });

const OBJECT_STORE_NAME = 'e11-object-store';

class FieldValue {
    static { this.increment = increment; }
    static { this.serverTimestamp = serverTimestamp; }
    static { this.documentId = documentId; }
    static { this.arrayUnion = arrayUnion; }
    static { this.arrayRemove = arrayRemove; }
    static { this.deleteField = deleteField; }
}

var OrderByDirection;
(function (OrderByDirection) {
    OrderByDirection["Asc"] = "asc";
    OrderByDirection["Desc"] = "desc";
})(OrderByDirection || (OrderByDirection = {}));

const addMessagesToTree = (tree, data) => data.reduce((accumulator, value) => {
    if (value.type === 'modified') {
        return accumulator.remove(value.item).insert(value.item, value);
    }
    else if (value.type === 'added') {
        const itemInTree = accumulator.find(value.item);
        return itemInTree !== null ? accumulator : accumulator.insert(value.item, value);
    }
    else {
        return accumulator.remove(value.item);
    }
}, tree);
const compareItems = (key, direction) => (key1, key2) => {
    if (key1.id === key2.id) {
        return 0;
    }
    if (key1[key] > key2[key]) {
        return direction === OrderByDirection.Asc ? 1 : -1;
    }
    else if (key1[key] < key2[key]) {
        return direction === OrderByDirection.Asc ? -1 : 1;
    }
    return key1.id.localeCompare(key2.id);
};

/**
 * Scroller meant to work for something like messaging where you have new items being inserted at the top but you can scroll back as far as you want.
 * Changes will be tracked for the first N items as it would be impractical to handle listeners to all items.
 */
class InfiniteScroller {
    constructor(getLatest, getMore, deserialize, orderByKey, orderDirection, transform = (item) => item) {
        this.getLatest = getLatest;
        this.getMore = getMore;
        this.deserialize = deserialize;
        this.done$ = new BehaviorSubject(false);
        this.subscriber = new Subject();
        this._done = false;
        this.subscriptions = [];
        this.getQuerySnapshotData = (doc) => {
            return { id: doc.id, ...this.deserialize(doc.data()) };
        };
        const compareFn = compareItems(orderByKey, orderDirection);
        this.items = this.subscriber.pipe(map((docs) => docs.map((model) => ({ ...model, item: this.getQuerySnapshotData(model.doc) }))), scan(addMessagesToTree, new RedBlackTree(compareFn)), tap((tree) => {
            if (orderDirection === OrderByDirection.Asc) {
                this.oldestRetrievedItem = tree.begin?.value;
            }
            else {
                this.oldestRetrievedItem = tree.end?.value;
            }
        }), map((docs) => docs.keys.map((doc) => transform(doc))));
        this.subscriptions.push(this.getLatest()
            .pipe(map((docs) => docs.map((change) => ({ doc: change.payload.doc, type: change.type }))))
            .subscribe((item) => this.subscriber.next(item)));
    }
    get done() {
        return this._done;
    }
    more() {
        if (this.done) {
            return;
        }
        this.subscriptions.push(this.getMore(this.oldestRetrievedItem?.doc)
            .pipe(take(1), tap((results) => {
            if (results.empty || (results.docs.length === 1 && results.docs[0].data().id === this.oldestRetrievedItem?.doc?.data()?.id)) {
                this._done = true;
                this.done$.next(true);
                this.done$.complete();
            }
        }), map((results) => results.docs.map((doc) => ({ doc, type: 'added' }))))
            .subscribe((items) => {
            this.subscriber.next(items);
        }));
    }
    tearDown() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriber.complete();
    }
}

function deserialize(doc) {
    const converted = DateService.convertAllDateFields(doc);
    return converted;
}

class QueryConstraintWeight {
    static { this.weight = new Map([
        ['orderBy', 0],
        ['where', 1],
        ['limit', 2],
        ['limitToLast', 2],
        ['startAt', 3],
        ['startAfter', 4],
        ['endAt', 5],
        ['endBefore', 6],
    ]); }
    static get(type) {
        return this.weight.get(type) ?? 0;
    }
    static diff(a, b) {
        return this.get(a) - this.get(b);
    }
}

/**
 * A query builder for  Firestore queries
 */
class FirestoreQueryBuilder {
    constructor(qConstraints) {
        this.qConstraints = [];
        if (qConstraints)
            this.qConstraints = qConstraints;
    }
    /**
     * Filter documents by the specified field and value.
     *
     * @param {IFieldType<T>} fieldPath Path to a field in a document
     * @param {WhereFilterOp} opStr A comparison operation
     * @param {unknown} value The value to compare against
     * @returns  FirestoreQueryBuilder}
     */
    where(fieldPath, opStr, value) {
        this.qConstraints = [...this.qConstraints, where(fieldPath, opStr, value)];
        return this;
    }
    /**
     * Sort by the specified field, in descending or ascending order.
     *
     * @param {IFieldType<T>} field The field to order by
     * @param  OrderByDirection} directionStr The direction to order by
     * @returns  FirestoreQueryBuilder}
     */
    orderBy(field, directionStr = OrderByDirection.Asc) {
        this.qConstraints = [...this.qConstraints, orderBy(field, directionStr)];
        return this;
    }
    /**
     * Creates a QueryLimitConstraint that only returns the last matching documents.
     *
     * You must specify at least one orderBy clause for limitToLast queries, otherwise an exception will be thrown during execution.
     *
     * @param {number} queryLimit The maximum number of items to return
     * @returns  FirestoreQueryBuilder}
     */
    limit(queryLimit) {
        this.qConstraints = [...this.qConstraints, limit(queryLimit)];
        return this;
    }
    /**
     * Creates a QueryLimitConstraint that only returns the last matching documents.
     *
     * You must specify at least one orderBy clause for limitToLast queries, otherwise an exception will be thrown during execution.
     *
     * @param {number} queryLimit The maximum number of items to return
     * @returns  FirestoreQueryBuilder}
     */
    limitToLast(queryLimit) {
        this.qConstraints = [...this.qConstraints, limitToLast(queryLimit)];
        return this;
    }
    /**
     * Results start at the provided document (inclusive).
     *
     * @param {unknown[]} fieldValues The field values to start at
     * @returns  FirestoreQueryBuilder}
     */
    startAt(...fieldValues) {
        this.qConstraints = [...this.qConstraints, startAt(...fieldValues)];
        return this;
    }
    /**
     * Results start after the provided document (exclusive).
     *
     * @param {unknown[]} fieldValues The field values to start after
     * @returns  FirestoreQueryBuilder}
     */
    startAfter(...fieldValues) {
        const filteredFieldValues = filterNullish(fieldValues);
        if (filteredFieldValues.length === 0) {
            return this;
        }
        this.qConstraints = [...this.qConstraints, startAfter(...filteredFieldValues)];
        return this;
    }
    /**
     * Results end at the provided document (inclusive).
     *
     * @param {unknown[]} fieldValues The field values to end at
     * @returns  FirestoreQueryBuilder}
     */
    endAt(...fieldValues) {
        this.qConstraints = [...this.qConstraints, endAt(...fieldValues)];
        return this;
    }
    /**
     * Results end before the provided document (exclusive).
     *
     * @param {unknown[]} fieldValues The field values to end before
     * @returns  FirestoreQueryBuilder}
     */
    endBefore(...fieldValues) {
        this.qConstraints = [...this.qConstraints, endBefore(...fieldValues)];
        return this;
    }
    /**
     * Sets the cache key for the query
     *
     * @param {string} cacheKey
     * @returns  FirestoreQueryBuilder}
     */
    setCacheKey(cacheKey) {
        this.cacheKey = cacheKey;
        return this;
    }
    /**
     * Builds the query
     *
     * @returns  QueryObj} The built query
     */
    build() {
        return {
            qConstraints: this.sortConstraints(this.qConstraints),
            cacheKey: this.cacheKey,
        };
    }
    sortConstraints(qConstraints) {
        return qConstraints.sort((a, b) => QueryConstraintWeight.diff(a.type, b.type));
    }
}

function toE11FirestoreError(err, method, path, model) {
    if (err.code === 'permission-denied') {
        const e11Err = toE11Error(err, {
            message: `Error performing ${method} operation on ${path}. Missing permission.`,
            title: `Error performing ${method} operation. Missing permission.`,
            type: E11_STANDARD_ERRORS.MISSING_PERMISSION,
            additionalData: { model, path, code: err.code },
        });
        return Promise.reject(e11Err);
    }
    else {
        const e11Err = toE11Error(err, {
            message: `Error performing ${method} operation on ${path}.`,
            title: `Error performing ${method} operation.`,
            type: ERROR_TYPES.FIRESTORE_UNKNOWN,
            additionalData: { code: err.code, path, model },
        });
        return Promise.reject(e11Err);
    }
}

class CacheRepository {
    constructor(databaseName) {
        this.actionSubject = new BehaviorSubject(null);
        this.database$ = from(openDB(databaseName, 3, {
            upgrade(db) {
                db.createObjectStore(OBJECT_STORE_NAME);
            },
        })).pipe(shareReplay(1));
        this.deleteSubscription$ = this.actionSubject
            .pipe(filter((item) => item?.type === 'delete-request'), withLatestFrom(this.database$))
            .subscribe(([req, db]) => {
            const deleteRequest = req;
            db.delete(deleteRequest.storeName, deleteRequest.key);
        });
        this.putSubscription$ = this.actionSubject
            .pipe(filter((item) => item?.type === 'put-request'), withLatestFrom(this.database$))
            .subscribe(([req, db]) => {
            const putRequest = req;
            db.put(putRequest.storeName, putRequest.value, putRequest.key);
        });
        this.objectStoreSubscription$ = this.actionSubject
            .pipe(filter((item) => item?.type === 'object-store-request'), withLatestFrom(this.database$))
            .subscribe(([req, db]) => {
            const objectStoreRequest = req;
            if (!db.objectStoreNames.contains(objectStoreRequest.storeName)) {
                db.createObjectStore(objectStoreRequest.storeName);
            }
        });
        this.clearSubscription$ = this.actionSubject
            .pipe(filter((item) => item?.type === 'clear'), switchMap(() => this.database$))
            .subscribe((db) => {
            db.clear(OBJECT_STORE_NAME);
        });
    }
    tearDown() {
        this.putSubscription$.unsubscribe();
        this.deleteSubscription$.unsubscribe();
        this.objectStoreSubscription$.unsubscribe();
        this.clearSubscription$.unsubscribe();
    }
    get(key, config) {
        return this.database$.pipe(switchMap((db) => db.objectStoreNames.contains(OBJECT_STORE_NAME)
            ? from(db.get(OBJECT_STORE_NAME, this.buildKey(key, config)))
            : of(undefined)), map((item) => (item && item.expired > new Date() ? item.item : undefined)));
    }
    put(key, value, config) {
        this.actionSubject.next({
            key: this.buildKey(key, config),
            storeName: OBJECT_STORE_NAME,
            value: { expired: DateService.add(new Date(), config.ttl?.value ?? 5, config.ttl?.units ?? 'seconds'), item: value },
            type: 'put-request',
        });
    }
    del(key, config) {
        this.actionSubject.next({ key: this.buildKey(key, config), storeName: OBJECT_STORE_NAME, type: 'delete-request' });
    }
    clear() {
        this.actionSubject.next({ type: 'clear' });
    }
    buildKey(key, cacheConfig) {
        return `${cacheConfig.prefix}_${key}`;
    }
}

class BaseFirestoreRepository {
    constructor(db) {
        this.db = db;
        this.fs = inject(Firestore);
    }
    /**
     * The method to deserialize the document into a Model.
     *
     * @param doc the document coming from firestore
     */
    deserialize(doc) {
        return deserialize(doc);
    }
    /**
     * Called before serialization, ahead of add and set methods.
     * Should contain validation logic to guarantee your desired attributes are on the model.
     * By default throws an error if your model does not have an id, except for the add method, which can generate its own id.
     *
     * @param model
     */
    validateOnCreate(model) {
        if (model.id === undefined) {
            throw e11Error({ type: ERROR_TYPES.DOCUMENT_MISSING_ID, title: 'Missing ID', additionalData: { model } });
        }
    }
    /**
     *
     * @returns a generated Firestore Document Id.
     */
    createId() {
        return doc(collection(this.fs, '_')).id;
    }
    /**
     * Get the document and listen to changes
     *
     * @param documentPath
     * @returns an observable of type T
     */
    _getValueChanges(documentPath) {
        const docRef = doc(this.fs, documentPath);
        return new Observable((observer) => {
            const unsubscribe = onSnapshot(docRef, (snapshot) => {
                if (snapshot.exists()) {
                    observer.next(this.deserialize(snapshot.data()));
                }
                else {
                    observer.next(undefined);
                }
            }, (error) => observer.error(toE11FirestoreError(error, 'getValueChanges', documentPath)));
            return () => unsubscribe();
        });
    }
    /**
     * Get the document once.
     *
     * @param documentPath to the document
     * @returns an observable of type T
     */
    _get(documentPath, transaction) {
        let getResponse$;
        if (this.db && this.cacheConfig && !transaction) {
            getResponse$ = this.db.get(documentPath, this.cacheConfig);
        }
        else {
            getResponse$ = of(undefined);
        }
        const docRef = doc(this.fs, documentPath);
        const obs = (transaction ? from(transaction.get(docRef)) : from(getDoc(docRef))).pipe(map((snapshot) => {
            if (snapshot.exists()) {
                return this.deserialize(snapshot.data());
            }
            return undefined;
        }), tap((document) => {
            if (this.db && this.cacheConfig && !transaction && document) {
                this.db.put(documentPath, document, this.cacheConfig);
            }
        }), catchError((err) => toE11FirestoreError(err, 'get', documentPath)));
        return getResponse$.pipe(switchMap((value) => iif(() => value !== undefined, getResponse$, obs)));
    }
    _getOrError(documentPath, transaction) {
        return this._get(documentPath, transaction).pipe(map((value) => {
            if (!value)
                throw e11Error({ type: ERROR_TYPES.DOCUMENT_NOT_FOUND, title: 'Document not found', additionalData: { documentPath } });
            return value;
        }));
    }
    _getValueChangesOrError(documentPath) {
        return this._getValueChanges(documentPath).pipe(map((value) => {
            if (!value)
                throw e11Error({ type: ERROR_TYPES.DOCUMENT_NOT_FOUND, title: 'Document not found', additionalData: { documentPath } });
            return value;
        }));
    }
    /**
     * Get the documents once.
     * Allows an optional query function which can be created with the FirestoreQueryBuilder
     *
     * @param collectionPath
     * @returns an observable array
     */
    _getAll(collectionPath, queryObj) {
        const { qConstraints, cacheKey } = queryObj ?? {};
        let cacheResponse$;
        if (cacheKey && this.db && this.cacheConfig) {
            cacheResponse$ = this.db.get(cacheKey, this.cacheConfig);
        }
        else {
            cacheResponse$ = of(undefined);
        }
        const ref = this._buildRef(collectionPath, qConstraints);
        const refOrQuery = from(getDocs(ref)).pipe(map((list) => this.mapQuerySnapshot(list)), tap((list) => {
            if (cacheKey && this.db && this.cacheConfig) {
                this.db.put(cacheKey, list, this.cacheConfig);
            }
        }), catchError((err) => toE11FirestoreError(err, 'getAll', collectionPath)));
        return cacheResponse$.pipe(switchMap((value) => iif(() => value !== undefined, of(value), refOrQuery)));
    }
    /**
     * Get the documents and listen to changes
     * Allows an optional query function which can be created with the FirestoreQueryBuilder
     *
     * @param collectionPath
     * @returns an observable array of type T
     */
    _getAllValueChanges(collectionPath, queryObj) {
        const { qConstraints } = queryObj ?? {};
        const ref = this._buildRef(collectionPath, qConstraints);
        return new Observable((observer) => {
            const unsubscribe = onSnapshot(ref, (snapshot) => {
                const data = snapshot.docs.map((doc) => this.deserialize({ id: doc.id, ...doc.data() }));
                observer.next(this.mapList(data));
            }, (error) => observer.error(toE11FirestoreError(error, 'getAllValueChanges', collectionPath)));
            return () => unsubscribe();
        });
    }
    _buildRef(collectionPath, qConstraints) {
        const colRef = collection(this.fs, collectionPath);
        return query(colRef, ...(qConstraints ?? []));
    }
    _query(collectionPath, queryObj) {
        let cacheResponse$;
        if (queryObj.cacheKey && this.db && this.cacheConfig) {
            cacheResponse$ = this.db.get(queryObj.cacheKey, this.cacheConfig);
        }
        else {
            cacheResponse$ = of(undefined);
        }
        const query$ = this._querySnapshot(collectionPath, queryObj).pipe(map((list) => this.mapQuerySnapshot(list)), tap((list) => {
            if (this.db && queryObj.cacheKey && this.cacheConfig) {
                this.db.put(queryObj.cacheKey, list, this.cacheConfig);
            }
        }), catchError((err) => toE11FirestoreError(err, 'query', collectionPath)));
        return cacheResponse$.pipe(switchMap((value) => iif(() => value !== undefined, of(value), query$)));
    }
    _querySnapshot(collectionPath, queryObj) {
        const { qConstraints } = queryObj ?? {};
        const ref = this._buildRef(collectionPath, qConstraints);
        const queryResponse = from(getDocs(ref));
        return queryResponse.pipe(catchError((err) => toE11FirestoreError(err, 'querySnapshot', collectionPath)));
    }
    _queryCount(collectionPath, queryObj) {
        const { qConstraints } = queryObj ?? {};
        const ref = this._buildRef(collectionPath, qConstraints);
        return from(getCountFromServer(ref)).pipe(map((count) => count.data().count), catchError((err) => toE11FirestoreError(err, 'aggregateCount', collectionPath)));
    }
    _queryValueChanges(collectionPath, queryObj) {
        const { qConstraints } = queryObj ?? {};
        const ref = this._buildRef(collectionPath, qConstraints);
        return new Observable((observer) => {
            const unsubscribe = onSnapshot(ref, (snapshot) => {
                const data = snapshot.docs.map((doc) => this.deserialize({ id: doc.id, ...doc.data() }));
                observer.next(this.mapList(data));
            }, (error) => observer.error(toE11FirestoreError(error, 'queryValueChanges', collectionPath)));
            return () => unsubscribe();
        });
    }
    _queryStateChanges(collectionPath, queryObj) {
        const { qConstraints } = queryObj ?? {};
        const ref = this._buildRef(collectionPath, qConstraints);
        return new Observable((observer) => {
            const unsubscribe = onSnapshot(ref, (snapshot) => {
                const data = snapshot.docChanges().map((change) => ({
                    type: change.type,
                    payload: {
                        doc: change.doc,
                        oldIndex: change.oldIndex,
                        newIndex: change.newIndex,
                    },
                }));
                observer.next(data);
            }, (error) => observer.error(toE11FirestoreError(error, 'queryStateChanges', collectionPath)));
            return () => unsubscribe();
        });
    }
    _getInfiniteScroll(collectionPath, queryObj, orderByKey, orderDirection, transform = (item) => item) {
        const runLatestQuery = () => this._queryStateChanges(collectionPath, queryObj);
        const getMoreQuery = (lastRetrieved) => {
            const newQuery = new FirestoreQueryBuilder(queryObj.qConstraints).startAfter(lastRetrieved ?? 0).build();
            return this._querySnapshot(collectionPath, newQuery);
        };
        const deserialize = (doc) => this.deserialize(doc);
        return new InfiniteScroller(runLatestQuery, getMoreQuery, deserialize, orderByKey, orderDirection, transform);
    }
    /**
     * Creates a new document of type T.
     * Sets the firestore document id to be the model.id if it exists, or generates a new one otherwise.
     * @param model
     * @param collectionPath to the collection
     * @returns
     */
    async _add(model, collectionPath, transaction) {
        const modelWithTimestamp = { ...model, __createdAt: new Date(), __updatedAt: new Date() };
        modelWithTimestamp.id = modelWithTimestamp.id ?? this.createId();
        this.validateOnCreate(modelWithTimestamp);
        const colRef = collection(this.fs, collectionPath);
        const docRef = doc(colRef, modelWithTimestamp.id);
        try {
            if (transaction) {
                await transaction.set(docRef, modelWithTimestamp);
            }
            else {
                if (docRef.id) {
                    await setDoc(docRef, modelWithTimestamp);
                }
                else {
                    await addDoc(colRef, modelWithTimestamp);
                }
            }
            return modelWithTimestamp;
        }
        catch (err) {
            return toE11FirestoreError(err, 'add', collectionPath, model);
        }
    }
    /**
     *
     * @param model
     * @param documentPath to the document
     * @returns
     */
    async _set(model, documentPath, transaction) {
        const existingModel = await firstValueFrom(this._get(documentPath));
        const modelWithTimestamp = {
            ...model,
            __createdAt: existingModel ? existingModel.__createdAt : new Date(),
            __updatedAt: new Date(),
        };
        this.validateOnCreate(modelWithTimestamp);
        const docRef = doc(this.fs, documentPath);
        try {
            if (transaction) {
                await transaction.set(docRef, modelWithTimestamp);
            }
            else {
                await setDoc(docRef, modelWithTimestamp);
            }
            return modelWithTimestamp;
        }
        catch (err) {
            return toE11FirestoreError(err, 'set', documentPath, model);
        }
    }
    /**
     * Updates if document exists. creates if document does not exist
     * A firestore Set with merge=true
     * @param model
     * @param documentPath to the document
     * @returns
     */
    async _upsert(model, documentPath, transaction) {
        const existingModel = await firstValueFrom(this._get(documentPath, transaction));
        const modelWithTimestamp = {
            ...model,
            __createdAt: existingModel ? existingModel.__createdAt : new Date(),
            __updatedAt: new Date(),
        };
        this.validateOnCreate(modelWithTimestamp);
        const docRef = doc(this.fs, documentPath);
        if (this.db && this.cacheConfig) {
            this.db.del(documentPath, this.cacheConfig);
        }
        try {
            if (transaction) {
                await transaction.set(docRef, modelWithTimestamp, { merge: true });
            }
            else {
                await setDoc(docRef, modelWithTimestamp, { merge: true });
            }
            return modelWithTimestamp;
        }
        catch (err) {
            return toE11FirestoreError(err, 'upsert', documentPath, model);
        }
    }
    /**
     * Update some fields of a document without overwriting the entire document.
     * Errors if document does not exist
     *
     * @param model
     * @param documentPath to the document
     * @returns
     */
    async _update(model, documentPath, transaction) {
        const modelWithTimestamp = { ...model, __updatedAt: new Date() };
        const docRef = doc(this.fs, documentPath);
        if (this.db && this.cacheConfig) {
            this.db.del(documentPath, this.cacheConfig);
        }
        try {
            if (transaction) {
                await transaction.update(docRef, modelWithTimestamp);
            }
            else {
                await updateDoc(docRef, modelWithTimestamp);
            }
        }
        catch (err) {
            return toE11FirestoreError(err, 'update', documentPath, model);
        }
    }
    /**
     * Delete a document
     *
     * @param documentPath to the document
     * @returns
     */
    async _delete(documentPath, transaction) {
        const docRef = doc(this.fs, documentPath);
        if (this.db && this.cacheConfig) {
            this.db.del(documentPath, this.cacheConfig);
        }
        try {
            if (transaction) {
                await transaction.delete(docRef);
            }
            else {
                await deleteDoc(docRef);
            }
        }
        catch (err) {
            Promise.reject(toE11FirestoreError(err, 'delete', documentPath));
        }
    }
    mapList(docs) {
        if (docs) {
            return docs.map((doc) => this.deserialize(doc));
        }
        return [];
    }
    mapQuerySnapshot(docs) {
        const mapped = [];
        if (docs && docs.size > 0) {
            docs.forEach((doc) => {
                mapped.push(this.deserialize(doc.data()));
            });
            return mapped;
        }
        return mapped;
    }
    async runTransaction(fn) {
        return await runTransaction(this.fs, async (t) => {
            return await fn(t);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseFirestoreRepository, deps: [{ token: CacheRepository, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseFirestoreRepository }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BaseFirestoreRepository, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CacheRepository, decorators: [{
                    type: Optional
                }] }] });

/**
 * Extendable by repositories to top-level collections to remove the boilerplate associated with CRUD operations on top-level collections
 */
class CollectionRepository extends BaseFirestoreRepository {
    constructor(cacheRepository) {
        super(cacheRepository);
    }
    buildPath(id = '') {
        return `${this.COLLECTION}${id}`;
    }
    get(id, transaction) {
        const path = this.buildPath(id);
        return super._get(path, transaction);
    }
    getOrError(id, transaction) {
        const path = this.buildPath(id);
        return super._getOrError(path, transaction);
    }
    getValueChanges(id) {
        const path = this.buildPath(id);
        return super._getValueChanges(path);
    }
    getValueChangesOrError(id) {
        const path = this.buildPath(id);
        return super._getValueChangesOrError(path);
    }
    /**
     * Get the documents once.
     *
     * @returns an observable array
     */
    getAll() {
        const path = this.buildPath();
        return super._getAll(path);
    }
    /**
     * Get the documents and listen to changes
     *
     * @param path
     * @returns an observable array of type T
     */
    getAllValueChanges() {
        const path = this.buildPath();
        return super._getAllValueChanges(path);
    }
    /**
     *
     * @param queryFn
     * @returns
     */
    query(query) {
        const path = this.buildPath();
        return super._query(path, query);
    }
    /**
     *
     * @param queryFn
     * @returns
     */
    queryValueChanges(queryFn) {
        const path = this.buildPath();
        return super._queryValueChanges(path, queryFn);
    }
    getInfiniteScroll(query, orderByKey = '__createdAt', orderDirection = OrderByDirection.Asc) {
        return this.getInfiniteScrollWithTransform(query, orderByKey, orderDirection);
    }
    getInfiniteScrollWithTransform(query, orderByKey, orderDirection, transform) {
        const path = this.buildPath();
        return super._getInfiniteScroll(path, query, orderByKey, orderDirection, transform);
    }
    /**
     * Creates a new document of type T.
     * Sets the firestore document id to be the model.id if it exists, or generates a new one otherwise.
     * @param model
     * @returns
     */
    add(model, transaction) {
        const path = this.buildPath();
        return super._add(model, path, transaction);
    }
    set(model, transaction) {
        const path = this.buildPath(model.id);
        return super._set(model, path, transaction);
    }
    /**
     * Updates if document exists. creates if document does not exist
     * A firestore Set with merge=true
     * @param model
     * @param transaction
     * @returns
     */
    upsert(model, transaction) {
        const path = this.buildPath(model.id);
        return super._upsert(model, path, transaction);
    }
    /**
     * Update some fields of a document without overwriting the entire document.
     *
     * @param model the fields needing to be updated on the document. Must include id in order to know which document to update
     * @returns
     */
    update(model, transaction) {
        const path = this.buildPath(model.id);
        return super._update(model, path, transaction);
    }
    /**
     * Delete a document
     *
     * @param id
     * @returns
     */
    delete(id, transaction) {
        const path = this.buildPath(id);
        return super._delete(path, transaction);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CollectionRepository, deps: [{ token: CacheRepository, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CollectionRepository }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CollectionRepository, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: CacheRepository, decorators: [{
                    type: Optional
                }] }] });

class SubcollectionRepository extends BaseFirestoreRepository {
    _buildPath(...ids) {
        return this.buildPath(...ids);
    }
    get(...ids) {
        const documentPath = this._buildPath(...ids);
        return super._get(documentPath);
    }
    getOrError(...ids) {
        const documentPath = this._buildPath(...ids);
        return super._getOrError(documentPath);
    }
    getValueChanges(...ids) {
        const documentPath = this._buildPath(...ids);
        return super._getValueChanges(documentPath);
    }
    getValueChangesOrError(...ids) {
        const documentPath = this._buildPath(...ids);
        return super._getValueChangesOrError(documentPath);
    }
    getAll(...ids) {
        const collectionPath = this._buildPath(...ids);
        return super._getAll(collectionPath);
    }
    getAllValueChanges(...ids) {
        const collectionPath = this._buildPath(...ids);
        return super._getAllValueChanges(collectionPath);
    }
    query(query, ...ids) {
        const collectionPath = this._buildPath(...ids);
        return super._query(collectionPath, query);
    }
    queryValueChanges(query, ...ids) {
        const collectionPath = this._buildPath(...ids);
        return super._queryValueChanges(collectionPath, query);
    }
    getInfiniteScroll(query, orderByKey, orderDirection, ...ids) {
        return this.getInfiniteScrollWithTransform(query, orderByKey, orderDirection, (item) => item, ...ids);
    }
    getInfiniteScrollWithTransform(query, orderByKey, orderDirection, transform, ...ids) {
        const collectionPath = this._buildPath(...ids);
        return super._getInfiniteScroll(collectionPath, query, orderByKey, orderDirection, transform);
    }
    /**
     * Creates a new document of type T.
     * If the document exists, it will be overwritten
     * If the document does not exist, it will be created.
     * Sets the firestore document id to be the model.id if it exists, or generates a new one otherwise.
     * @param model
     * @param ids the ids needed to construct the path to the collection
     * @returns
     */
    add(model, ...ids) {
        const collectionPath = this._buildPath(...ids);
        return super._add(model, collectionPath);
    }
    /**
     * If the document exists, it will be overwritten.
     * If the document does not exist, it will be created.
     * @param model
     * @param ids the ids needed to construct the path to the document
     * @returns
     */
    set(model, ...ids) {
        const documentPath = this._buildPath(...ids);
        return super._set(model, documentPath);
    }
    /**
     * Updates if document exists. creates if document does not exist
     * A firestore Set with merge=true
     * @param model
     * @param ids
     * @returns
     */
    upsert(model, ...ids) {
        const documentPath = this._buildPath(...ids);
        return super._upsert(model, documentPath);
    }
    /**
     * Errors if document does not exist
     * @param model
     * @param ids
     * @returns
     */
    update(model, ...ids) {
        const documentPath = this._buildPath(...ids);
        return super._update(model, documentPath);
    }
    delete(...ids) {
        const documentPath = this._buildPath(...ids);
        return super._delete(documentPath);
    }
}

function runCollectionGroupQuery(fs, collection, qConstraints) {
    const collRef = collectionGroup(fs, collection.replace(/\//g, ''));
    const queryRef = query(collRef, ...qConstraints);
    return from(getDocs(queryRef)).pipe(map$1((snapshot) => snapshot.docs.map((doc) => deserialize(doc.data()))), catchError$1((err) => toE11FirestoreError(err, 'runCollectionGroupQuery', collection)));
}

/**
 * Represents a node in the linked list.
 * @template T The type of the data stored in the node.
 */
class Node {
    /**
     * Creates a new Node.
     * @param {T} data - The data to store in the node.
     * @param {Node<T> | null} previous - The previous node in the list.
     * @param {Node<T> | null} next - The next node in the list.
     */
    constructor(data, previous, next) {
        this.data = data;
        this.previous = previous;
        this.next = next;
    }
}
/**
 * A tracking linked list that keeps track of the current position and allows navigation.
 * @template T The type of the data stored in the linked list.
 */
class TrackingLinkedList {
    constructor() {
        this._current = null;
        this.end = null;
    }
    /**
     * Gets the data of the current node.
     * @returns {T | undefined} The data of the current node, or undefined if there is no current node.
     */
    get current() {
        return this._current?.data;
    }
    /**
     * Checks if the current node is the start of the list.
     * @returns {boolean} True if at the start of the list; otherwise, false.
     */
    get isAtStart() {
        return isNil(this._current?.previous);
    }
    /**
     * Checks if the current node is the end of the list.
     * @returns {boolean} True if at the end of the list; otherwise, false.
     */
    get isAtEnd() {
        return isNil(this._current?.next);
    }
    /**
     * Moves to the next node in the list.
     * @returns {T | null} The data of the next node, or null if at the end of the list.
     */
    next() {
        if (!this._current?.next) {
            return null;
        }
        this._current = this._current.next;
        return this.current;
    }
    /**
     * Moves to the previous node in the list.
     * @returns {T | null} The data of the previous node, or null if at the start of the list.
     */
    previous() {
        if (!this._current?.previous) {
            return null;
        }
        this._current = this._current.previous;
        return this.current;
    }
    /**
     * Adds a new item to the end of the list.
     * @param {T} item - The data to add to the list.
     * @param {boolean} setCurrent - Whether to set the new item as the current node.
     */
    push(item, setCurrent) {
        const newNode = new Node(item, this.end, null);
        if (this.end) {
            this.end.next = newNode;
        }
        if (setCurrent) {
            this._current = newNode;
        }
        this.end = newNode;
    }
    /**
     * Remove the last item from the list, and move the current node to the previous item.
     */
    pop() {
        if (!this.end) {
            return null;
        }
        const last = this.end;
        this.end = last.previous;
        if (this._current === last) {
            this._current = this.end;
        }
        return last.data;
    }
    /**
     * Clears the list.
     */
    clear() {
        this._current = null;
        this.end = null;
    }
}

class Pager {
    constructor(getMore, deserialize) {
        this.getMore = getMore;
        this.deserialize = deserialize;
        this.cache = new TrackingLinkedList();
        this.lastItem = null;
        this.isDone = false;
    }
    async nextPage() {
        const nextPage = this.cache.next();
        let docsToReturn;
        if (nextPage) {
            docsToReturn = nextPage;
        }
        else {
            docsToReturn = await this.loadPage(true);
        }
        if (this.cache.isAtEnd) {
            await this.loadPage(false);
        }
        return docsToReturn;
    }
    async loadPage(setCurrent) {
        if (this.isDone) {
            return this.cache.current ?? [];
        }
        const page = await firstValueFrom(this.getMore(this.lastItem));
        const docs = page.docs;
        if (docs.length === 0) {
            this.isDone = true;
            return this.cache.current ?? [];
        }
        this.lastItem = last(docs) ?? null;
        const mappedDocuments = docs.map((doc) => this.deserialize(doc.data()));
        this.cache.push(mappedDocuments, setCurrent);
        return mappedDocuments;
    }
    async previousPage() {
        return this.cache.previous();
    }
    get hasNext() {
        return !this.cache.isAtEnd;
    }
    get hasPrevious() {
        return !this.cache.isAtStart;
    }
}

/*
 * Public API Surface of web-api-firebase
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsService, AuthUtilService, BaseFirestoreRepository, CacheRepository, CollectionRepository, FieldValue, FirebaseFunctionsService, FirestoreQueryBuilder, InfiniteScroller, OBJECT_STORE_NAME, OrderByDirection, Pager, RemoteConfigService, StorageService, SubcollectionRepository, runCollectionGroupQuery };
